import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { normalizeCourses, flattenMeetings } from '../utils/schedule';

const ScheduleContext = createContext({
  courses: [],
  meetings: [],
  loading: true,
  error: null,
  refetch: () => Promise.resolve(),
});

export const ScheduleProvider = ({ children }) => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ format: 'list', date_range: 'all' });
      const res = await fetch(`/vu-connect/api/course-schedule?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to fetch course schedule: ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load course schedule');
      setCourses(normalizeCourses(json.data));
    } catch (e) {
      setError(e.message);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && !hasFetched.current) {
      hasFetched.current = true;
      fetchSchedule();
    }
  }, [user, fetchSchedule]);

  const meetings = useMemo(() => flattenMeetings(courses), [courses]);

  return (
    <ScheduleContext.Provider value={{ courses, meetings, loading, error, refetch: fetchSchedule }}>
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = () => useContext(ScheduleContext);
